/* eslint-disable camelcase */
import { snakeCase } from "lodash-es";

export const OFFER_STATUSES = {
  APPLYING: "applying",
  IN_WAITLIST: "in waitlist",
  NOT_SELECTED: "not selected",
  WAITING_PRODUCT: "waiting product",
  PENDING_CONTENT_SUBMISSION: "pending content submission",
  CONTENT_WORK_NEEDED: "content work needed",
  WAITING_CONTENT_APPROVAL: "waiting content approval",
  PENDING_POST: "pending post",
  PENDING_COMPLETION: "pending completion",
  WORK_NEEDED: "work needed",
  PENDING_EVENT: "pending event",
  ATTEND_EVENT: "attend event",
  PENDING_REVIEW: "pending review",
  PENDING_STATS: "pending stats",
  WAITING_REWARD: "waiting reward",
  DONE: "done",
  REMOVED: "removed",
  DECLINED: "declined",
  CANCELED: "canceled",
  GHOSTED: "ghosted",
};

export const OFFER_STAGE = {
  APPLYING: [OFFER_STATUSES.APPLYING, OFFER_STATUSES.IN_WAITLIST],
  CONTENT_APPROVAL: [
    OFFER_STATUSES.PENDING_CONTENT_SUBMISSION,
    OFFER_STATUSES.CONTENT_WORK_NEEDED,
    OFFER_STATUSES.WAITING_CONTENT_APPROVAL,
  ],
  POST_CONTENT: [
    OFFER_STATUSES.PENDING_POST,
    OFFER_STATUSES.PENDING_COMPLETION,
    OFFER_STATUSES.WORK_NEEDED,
  ]
};

/**
 * Enum to know the status of a content posted by influncer
 * @readonly
 * @enum {string}
 */
export const CONTENT_STATUSES = {
  REVIEW: "review",
  REJECTED: "rejected",
  FIRED: "fired",
  NOT_SELECTED: "not_selected",
  ACCEPTED: "accepted",
  HIRED: "hired",
  ACCEPTED_BUT: "accepted_but",
  WAITING_AM: "waiting_am",
  WAITING_CLIENT: "waiting_client",
  PENDING_INFLUENCER: "pending_influencer",
  PENDING: "pending",
  BACKUP: "backup",
  NEEDS_REVIEW: "needs_review",
  NEEDS_SUBMISSION: "needs_submission",
  NEEDS_MORE_INFORMATION: "needs_more_information"
};

export const CONTENT_REVIEWED_STATUSES = [
  CONTENT_STATUSES.REJECTED,
  CONTENT_STATUSES.ACCEPTED,
  CONTENT_STATUSES.ACCEPTED_BUT
];

export const contentApprovalMetrics = [
  {
    color: 'primary', name: 'total_drafts_count',
    label: 'Drafts', link: '/manager/campaigns/:campaign_id/dashboard/content_approval/in_mainlist/:user_offer_id',
  },
  {
    color: 'danger', name: 'rejected_drafts_count',
    label: 'Rejected', link: `/manager/campaigns/:campaign_id/dashboard/content_approval/in_mainlist/:user_offer_id?status=rejected`
  },
  {
    color: 'success', name: 'accepted_drafts_count',
    label: 'Approved', link: `/manager/campaigns/:campaign_id/dashboard/content_approval/in_mainlist/:user_offer_id?status=accepted`
  }
];

export const pendingReviewMetrics = [
  { color: 'primary', name: 'deliverable_posts_count', label: 'Deliverables', link: `/manager/campaigns/:campaign_id/dashboard/review/in_mainlist/:user_offer_id` },
  { color: 'success', name: 'reviewed_posts_count', label: 'Reviewed', link: `/manager/campaigns/:campaign_id/dashboard/review/in_mainlist/:user_offer_id?status=reviewed` }
];

export const STATS_STATUSES = {
  NEEDS_INSIGHTS: "needs_insights",
  COMPLETE: "complete",
  UNDER_REVIEW: "under_review",
  RETRIEVING_POST: "retrieving_post",
};

export const CONTENT_APPROVAL_OPTIONS = {
  NO_CONTENT_APPROVAL: "no_content_approval",
  CAPTION_APPROVAL_ONLY: "caption_approval_only",
  CAPTION_AND_CONTENT_APPROVAL: "caption_and_content_approval",
  GOOGLE_WEB_STORY: "google_web_story",
  YOUTUBE_SHORTS: "youtube_reels"
};

export const PLATFORMS = ['instagram', 'tiktok', 'youtube', 'pinterest', 'twitter', 'other', 'google_web_story'];
export const DELIVERY_TYPES = ['in_feed', 'stories', 'reels_only', 'in_app', 'other'];

export const ACTIONS = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  ERROR: 'ERROR',
};

export const GENDER_TYPES = ['Male', 'Female', 'Nonbinary'];

export const USER = {
  INFLUENCERS: 'influencers',
  CLIENTS: 'clients',
  ADMINS: 'admins',
};
export const i18CODE_EXCEPTIONS = { 'Nonbinary': 'non_binary' };

export const toI18Code = (value) => (value in i18CODE_EXCEPTIONS) ? i18CODE_EXCEPTIONS[value] : snakeCase(value);

export const REPORTING_POST_METRICS = {
  INST: [
    { key: 'likes' },
    { key: 'comments' },
    { key: 'engagement_rate_over_followers' },
    { key: 'play_to_follower_ratio' },
    { key: 'shares' }
    // { key: 'saves' }
  ],
  INST_STORY: [
    { key: 'impressions' },
    { key: 'replies' },
    { key: 'swipe_ups' },
    { key: 'shares' },
    { key: 'likes' }
  ],
  INST_VIDEO: [
    { key: 'likes' },
    { key: 'comments' },
    { key: 'engagement_rate_over_watches' },
    { key: 'plays' },
    { key: 'play_to_follower_ratio' },
    { key: 'shares' }
    // { key: 'saves' }
  ],
  TIKT: [
    { key: 'likes' },
    { key: 'comments' },
    { key: 'views' },
    { key: 'shares' },
    { key: 'saves' },
    // Hidden for ENG-9155
    // { key: 'replies' },
    // { key: 'followers' },
    { key: 'engagement_rate_over_watches' },
    { key: 'view_to_follower_ratio', label: 'view_to_follower_ratio_short' }
  ],
  TWIT: [
    { key: 'likes' },
    { key: 'retweets' },
    { key: 'comments' },
    { key: 'replies' }
  ],
  PINT: [
    { key: 'likes' },
    { key: 'comments' },
    { key: 'repins' },
  ],
  LINK: [
    { key: 'comments' },
    { key: 'reactions' },
    { key: 'reposts' }
  ],
  YOUT: [
    { key: 'likes' },
    { key: 'comments' },
    { key: 'views' },
    { key: 'view_to_follower_ratio', label: 'views_to_subscribers' },
    { key: 'engagement_rate_over_watches' }
  ],
};

export const VETTING_STATUSES = {
  HIRED: 'hired',
  PENDING: 'pending',
  NOT_SELECTED: 'not_selected',
  FIRED: 'fired',
  BACKUP: 'backup',
};

export const SOCIAL_PLATFORMS = {
  instagram: {
    key: 'instagram',
    name: 'Instagram',
    backendKey: 'inst',
    icon: 'instagram',
    profileLink: "https://www.instagram.com/",
    iconClass: 'fab'
  },
  tiktok: {
    key: 'tiktok',
    name: 'TikTok',
    backendKey: 'tikt',
    icon: 'tiktok',
    profileLink: "https://www.tiktok.com/@",
    iconClass: 'fab'
  },
  youtube: {
    key: 'youtube',
    name: 'YouTube',
    backendKey: 'yout',
    icon: 'youtube',
    profileLink: "https://www.youtube.com/",
    iconClass: 'fab'
  },
  other: {
    key: 'other',
    name: 'Other',
    backendKey: 'othe',
    icon: 'globe',
    iconClass: 'fal'
  },
  linkedin: {
    key: 'linkedin',
    name: 'LinkedIn',
    backendKey: 'link',
    icon: 'linkedin',
    profileLink: "https://www.linkedin.com/in/",
    iconClass: 'fab'
  },
  google_web_stories: {
    key: 'google_web_stories',
    name: 'Google Web Stories',
    backendKey: 'goog',
    icon: 'google',
    iconClass: 'fab'
  },
};

export const SURVEY = {
  SURVEY_REPORT_LINK_HOST: 'obviouslysocial.typeform.com'
};

export const POST_MEDIA_TYPE = {
  POST: 'instagram_one',
  STORIES: 'instagram_stories_one',
  IN_FEED_POST: 'in_feed_posts',
  YOUTUBE_SHORTS: 'youtube_shorts',
  GOOGLE_WEB_STORY: 'google_web_story_one',
};

export const CONTENT_TYPE = {
  POST: 'post',
  STORY: 'story',
  REEL: 'reel',
  YOUTUBE_SHORT: 'youTubeShort',
  GOOGLE_WEB_STORY: 'googleWebStory',
};

export const M_ENDPOINTS = {
  S3: {
    // TODO remove
    POST: '/m/s3_image'
  }
}

export const UTIL_ENDPOINTS = {
  S3: {
    POST: '/s3_image'
  }
}

export const CONVERSATION_RICH_TEXT_OPTIONS = {
  minHeight: 90,
  maxHeight: 300,
  dialogsInBody: true,
  toolbar: [
    ['color', ['color']],
    ['para', ['ul', 'ol']],
    ['font', ['bold', 'italic', 'underline']],
    ['insert', ['link']]
  ]
}
